// Lightweight Auth + Session context stubs for blyn-agent.
// When embedded in cloudfront_ui, the host passes real auth via BylnAgentProps.
// Standalone (dev mode): reads token/userId from localStorage.

import { createContext, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'

// ── Auth ──────────────────────────────────────────────────────────────────────

export interface AuthData {
  isAuthenticated: boolean
  isLoading: boolean
  userId: string | null
  token: string | null
}

export const AuthContext = createContext<AuthData>({
  isAuthenticated: false,
  isLoading: false,
  userId: null,
  token: null,
})

export function AuthProvider({ children, auth }: { children: ReactNode; auth?: Partial<AuthData> }) {
  const [authData, setAuthData] = useState<AuthData>({ isAuthenticated: false, isLoading: true, userId: null, token: null })

  useEffect(() => {
    if (auth) {
      setAuthData({ isAuthenticated: false, isLoading: false, userId: null, token: null, ...auth })
      return
    }
    const token = localStorage.getItem('token')
    const userId = localStorage.getItem('userId')
    setAuthData({ isAuthenticated: !!(token && userId), isLoading: false, userId, token })
  }, [auth])

  return <AuthContext.Provider value={authData}>{children}</AuthContext.Provider>
}

export function useAuth() {
  return useContext(AuthContext)
}

// ── Session ───────────────────────────────────────────────────────────────────

export interface SessionUser {
  id?: string
  userID?: string | number
  userId?: string | number
  email?: string
  firstname?: string
  lastname?: string
  account_id?: string | number
  accountId?: string | number
  tenantId?: string | number
  [key: string]: unknown
}

export interface SessionData {
  user: SessionUser | null
  userLoading: boolean
  isDemo: boolean
}

export const SessionContext = createContext<SessionData>({
  user: null,
  userLoading: false,
  isDemo: false,
})

// Plain module-level mirror of the session user, kept in sync synchronously
// on every SessionProvider render (not via useEffect). Consumers that resolve
// tenant/user IDs during render or in a mount effect (e.g. MemoryPane) would
// otherwise race against the store update in layout.tsx's effect — React
// fires child effects before parent effects, so the store isn't populated
// yet on first read. This snapshot is available before any descendant renders.
let _sessionUserSnapshot: SessionUser | null = null

export function getSessionUserSnapshot(): SessionUser | null {
  return _sessionUserSnapshot
}

export function SessionProvider({ children, user }: { children: ReactNode; user?: SessionUser | null }) {
  _sessionUserSnapshot = user ?? null
  return (
    <SessionContext.Provider value={{ user: user ?? null, userLoading: false, isDemo: false }}>
      {children}
    </SessionContext.Provider>
  )
}

export function useSession() {
  return useContext(SessionContext)
}
